import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { RefineWorkbench } from "@studio-features/meta-crew/refine-workbench";
import type { RefineActions, RefineMutationResult, RefineView } from "@studio-features/meta-crew/refine-data";
import type { FileRef, FolderResult } from "@studio-features/meta-crew/library-adapter";
import { ApiError, api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { useCustomers } from "../lib/queries";
import { type RefineDetailDto, type RefineListQuery, type RefineRow, rowToJobList, useRefineDetail, useRefineInvalidate, useRefineList } from "../lib/refine-api";
import { useRefineUploadProvider } from "../lib/refine-upload";
import { HQ_SCOPE } from "../lib/library-adapter";
import type { Me } from "../lib/types";
import { urlParam } from "../console/metrics";
import { Loading, PanelError } from "../console/ui";
import { useLibraryAdapterFor } from "./manuscripts";

/**
 * 작업 › 정형화 (요청서 29·31절, 시안 24ca0e6 RefineWorkbench). docs/meta-crew-manuscript-refine-design.md.
 * 목록·상세·올리기·동작을 시안 부품이 그리고, 여기서는 서버 값을 view 로 바꾸고 API 를 actions 로 넣는다.
 * 올리기는 원고함과 같은 공통 창(두 채널): 정형화 채널은 src/lib/refine-upload.ts(패키지 batch), 완성 원고 채널은 원고함 어댑터.
 * 경로: /admin/refine?id=<작업> (상세) · ?create=1&customerId=&folderId= (올리기 창 바로) · ?ids=a,b (방금 만든 작업만). 진행 중 폴링은 refine-api 가 맡는다.
 */
const LIMIT = 30;
const ids = (files: FileRef[]) => files.map((f) => f.fileId);

export function RefineScreen({ me, onOpenManuscript, onOpenSettings }: { me: Me; onOpenManuscript: (id: string) => void; onOpenSettings: () => void }) {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(() => urlParam("id"));
  const [initialCreate] = useState(() => urlParam("create") === "1");
  const [uploadTarget] = useState(() => {
    const customerId = urlParam("customerId");
    return customerId ? { customerId, folderId: urlParam("folderId") } : undefined;
  });
  const [query, setQuery] = useState<RefineListQuery>(() => ({ q: "", customerId: "", status: "", offset: 0, limit: LIMIT, ids: urlParam("ids") ?? undefined }));
  const [accumulated, setAccumulated] = useState<{ key: string; items: RefineRow[] | null }>({ key: "", items: null });
  const canRead = can(me, "MANUSCRIPT_READ");
  const customers = useCustomers("", canRead);
  const list = useRefineList(query, canRead && !selectedId);
  const detail = useRefineDetail(selectedId);
  const invalidate = useRefineInvalidate();
  const setSearch = (search: Record<string, string>) => void navigate({ to: "/admin/$screen", params: { screen: "refine" }, search: search as never, replace: true });
  const showCreated = (created: string[]) => {
    const next = created.length ? created.join(",") : undefined;
    setAccumulated({ key: "", items: null });
    setQuery((q) => ({ ...q, offset: 0, ids: next }));
    setSearch(next ? { ids: next } : {});
  };
  const upload = useRefineUploadProvider(me, { onOpenList: showCreated, onOpenSettings, customerName: (id) => customers.data?.items.find((c) => c.id === id)?.name });
  // 완성 원고 채널은 원고함과 같은 어댑터 (시안 계약: libraryProvider 가 있어야 채널 전환이 열린다)
  const library = useLibraryAdapterFor(canRead);
  const customerItems = customers.data?.items ?? [];
  // 등록 대상 폴더 = 메타크루(관리자) 트리 (요청서 35-1). 목록·상세의 "대상 폴더" 이름도 여기서
  const hqFolders = useQuery({
    queryKey: ["admin", "manuscript-folders", HQ_SCOPE],
    enabled: canRead,
    staleTime: 60_000,
    queryFn: () => api.get<FolderResult>(`/admin/manuscript-folders?owner=${HQ_SCOPE}`),
  });
  const folderList = useMemo(() => (hqFolders.data?.folders ?? []).map((f) => ({ id: f.id, customerId: HQ_SCOPE, name: f.name })), [hqFolders.data]);

  if (!canRead) return <PanelError error="권한이 없습니다." />;
  if (customers.error) return <PanelError error={errorMessage(customers.error)} onRetry={() => void customers.refetch()} />;
  if (!customers.data) return <Loading />;

  // 더 읽기: 같은 필터면 이어 붙이고, 필터가 바뀌면 교체한다
  const filterKey = `${query.q}|${query.folderId ?? ""}|${query.status}|${query.ids ?? ""}`;
  const pageItems = list.data?.items ?? [];
  const items = query.offset > 0 && accumulated.key === filterKey && accumulated.items ? [...accumulated.items, ...pageItems.filter((r) => !accumulated.items!.some((a) => a.id === r.id))] : pageItems;
  const selected = selectedId ? detail.build(me) : undefined;
  const view: RefineView = {
    uploadTarget,
    jobs: rowToJobList(items, me),
    selected,
    total: list.data?.total ?? items.length,
    loading: selectedId ? detail.loading : list.isLoading,
    error: selectedId ? detail.error : list.error ? errorMessage(list.error) : undefined,
    customers: customerItems.map((c) => ({ id: c.id, name: c.name })),
    folders: folderList,
    guidelines: upload.guidelines,
    models: upload.models,
    readiness: upload.readiness,
    // 요청서 35-1: 파일 귀속(관리자)과 등록 폴더 트리(원고함과 같은 어댑터)
    hqCustomerId: HQ_SCOPE,
    folderTreeProvider: library.adapter ?? undefined,
    canCreate: can(me, "MANUSCRIPT_REFINE"),
  };
  const open = (id: string) => {
    setSelectedId(id);
    setSearch({ id });
  };
  const actions: RefineActions = {
    query: (q) => {
      // 검색·필터를 바꾸면 "방금 만든 작업만" 은 푼다 (이전 필터와 같으면 유지)
      const folderId = q.folderId ?? "";
      const sameFilter = q.q === query.q && folderId === (query.folderId ?? "") && q.status === query.status;
      const next: RefineListQuery = { q: q.q, customerId: "", folderId: folderId || undefined, status: q.status, offset: q.offset, limit: q.limit || LIMIT, ids: sameFilter ? query.ids : undefined };
      const nextKey = `${next.q}|${next.folderId ?? ""}|${next.status}|${next.ids ?? ""}`;
      // 더 읽기(offset > 0)면 지금 보이는 목록 뒤에 붙이고, 필터 변경(offset 0)이면 새로 읽는다
      setAccumulated(q.offset > 0 ? { key: nextKey, items } : { key: nextKey, items: null });
      setQuery(next);
      if (!sameFilter && query.ids) setSearch({});
    },
    open,
    back: () => {
      setSelectedId(null);
      setSearch(query.ids ? { ids: query.ids } : {});
      void invalidate();
    },
    reload: () => {
      upload.clearReadiness();
      void invalidate(selectedId ?? undefined);
    },
    // 시안 24ca0e6 뒤 새 올리기는 batch/showCreated 로 간다. create 는 이전 호환용 (단건 등록 API)
    create: async (input) => {
      try {
        const r = await api.post<RefineDetailDto>("/admin/manuscripts/refine", {
          customerId: input.customerId,
          folderId: input.folderId || undefined,
          title: input.title.trim() || undefined,
          sourceBody: input.sourceBody,
          files: ids(input.files),
          instructions: input.instructions.trim() || undefined,
          guidelineId: input.guidelineId || undefined,
          model: input.model || undefined,
        });
        upload.clearReadiness();
        await invalidate();
        open(r.id);
      } catch (e) {
        upload.fail(e);
      }
    },
    batch: upload.provider.batch,
    enqueue: upload.provider.enqueue,
    showCreated,
    mutate: async (id, action, input) => {
      try {
        let result: RefineMutationResult | undefined;
        if (action === "register") {
          // 요청서 32절: 연결된 원고의 새 버전이면 {asNew:false} 만 (폴더·상태·키워드는 원고 것 유지), 새 원고면 asNew:true + 입력값
          const body = input.asNew === false ? { asNew: false } : { folderId: input.folderId ?? null, state: input.state, keywords: input.keywords, ...(input.asNew === true ? { asNew: true } : {}) };
          const r = await api.post<{ revised?: RefineMutationResult["revised"]; created?: { manuscript: { id: string } } }>(`/admin/manuscripts/refine/${id}/register`, body);
          result = r.revised ? { revised: r.revised } : r.created ? { manuscriptId: r.created.manuscript.id } : undefined;
        } else if (action === "reject") await api.post(`/admin/manuscripts/refine/${id}/reject`, { reason: input.reason });
        else if (action === "rerun") await api.post(`/admin/manuscripts/refine/${id}/rerun`, { note: (input.note as string) || undefined, guidelineId: (input.guidelineId as string) || undefined, model: (input.model as string) || undefined });
        else if (action === "source") await api.put(`/admin/manuscripts/refine/${id}/source`, { sourceBody: input.sourceBody, files: ids((input.files as FileRef[]) ?? []), note: (input.note as string) || undefined, rerun: !!input.rerun });
        else if (action === "cancel") await api.post(`/admin/manuscripts/refine/${id}/cancel`);
        else if (action === "delete") await api.del(`/admin/manuscripts/refine/${id}`);
        await invalidate(id);
        if (action === "delete") {
          setSelectedId(null);
          setSearch({});
        }
        return result;
      } catch (e) {
        // 409(원고가 게시됨·발행 예정)는 시안이 "새 원고로 등록" 선택을 띄우도록 status 를 실어 보낸다
        try {
          upload.fail(e);
        } catch (err) {
          if (e instanceof ApiError) Object.assign(err as Error, { status: e.status });
          throw err;
        }
      }
    },
    uploadFile: upload.uploadFile,
    openManuscript: onOpenManuscript,
    openSettings: onOpenSettings,
  };
  const createdCount = query.ids ? query.ids.split(",").filter(Boolean).length : 0;
  return (
    <>
      {!selectedId && createdCount > 0 && (
        <p role="status" className="mc-created-only">
          방금 만든 정형화 작업 {createdCount}개만 보고 있습니다.
          <button type="button" onClick={() => showCreated([])}>
            전체 목록 보기
          </button>
        </p>
      )}
      <RefineWorkbench key={selectedId ?? "list"} view={view} actions={actions} libraryProvider={library.adapter ?? undefined} initialCreate={!selectedId && initialCreate} />
    </>
  );
}
